import React, { ReactNode, useState, useEffect, useRef } from 'react'
import { Button } from '@quotatisgroup/uikit-public.ui.button'

import './modal.scss'

export type ModalProps = {
  children?: ReactNode | string
  trigger: ReactNode
  title?: string
  hasDisableValidButton?: boolean
  validateModal?: () => void
  hasValidButton?: boolean
  validButtonText?: string
  hasCancelButton?: boolean
  cancelButtonText?: string
  closeModal?: () => void
  closingStep?: boolean
  setClosingStep?: (props: boolean) => void
  closingCallback?: () => void
  openCallback?: () => void
  id?: string
  closeBtnId?: string
  cancelBtnId?: string
}

export function Modal({
  children,
  trigger,
  title,
  hasDisableValidButton = false,
  validateModal,
  hasValidButton,
  validButtonText,
  hasCancelButton,
  cancelButtonText,
  closeModal,
  closingStep = true,
  setClosingStep,
  closingCallback,
  openCallback,
  id,
  closeBtnId,
  cancelBtnId,
}: ModalProps) {
  const [display, setDisplay] = useState(false)
  const modalInnerRef = useRef(null)

  const validated = () => {
    validateModal()
    if (closingStep) {
      setDisplay(false)
    } else {
      setClosingStep(true)
    }
  }

  const closing = () => {
    setDisplay(false)
    if (closingCallback && closingStep) {
      closingCallback()
    }
  }

  const open = () => {
    setDisplay(!display)
    if (openCallback) {
      openCallback()
    }
  }

  useEffect(() => {
    if (closeModal) {
      setDisplay(false)
    }
  }, [closeModal])

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        modalInnerRef.current &&
        !modalInnerRef.current.contains(event.target)
      ) {
        closing()
      }
    }

    document.addEventListener('mousedown', handleClickOutside, {
      passive: true,
    })
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [modalInnerRef, closing])

  return (
    <>
      <div
        className="trigger"
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={open}
      >
        {trigger}
      </div>
      {display && (
        <div className="modal">
          <div className="modal-inner" ref={modalInnerRef} id={id}>
            {title && <div className="title">{title}</div>}
            <div
              className="btn-close"
              role="button"
              id={closeBtnId}
              tabIndex={0}
              aria-label="open modal"
              onClick={closing}
              onKeyDown={closing}
            />
            <div className="children">{children}</div>
            {(hasValidButton || hasCancelButton) && (
              <div className="modal-footer">
                {hasValidButton && (
                  <Button
                    id="modal-button"
                    variant={hasDisableValidButton ? 'basic' : 'primary'}
                    size="md"
                    align="right"
                    handleClick={validated}
                    disabled={hasDisableValidButton}
                  >
                    {validButtonText}
                  </Button>
                )}
                {hasCancelButton && (
                  <Button
                    variant="white"
                    size="md"
                    align="right"
                    handleClick={closing}
                    id={cancelBtnId}
                  >
                    {cancelButtonText}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

Modal.triggerClose = (trigger = false) => {
  return trigger
}
